import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AuthContextProvider } from '@/hooks/use-auth';
import { ReactQueryProvider } from '@/providers/react-query-provider';
import { ToastProvider } from '@/providers/toast-provider';
import { MarketPlaceHeader } from '@/features/marketplace/components/header';
import { Footer } from '@/features/marketplace/components/footer';
import { PADDING_TOP_HEADER_HEIGHT } from '@/constants';
import { composeClassName } from '@/utils/util_helpers';
import { CitiesListContextProvider } from '@/features/marketplace/hooks/use-get-cities';
import { useLocale } from '@/hooks/use-locale';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { HouseCard } from '../../components/house-card';
import { useColocations } from '../../hooks/use-colocations';
import { useWatchToggle } from '../../hooks/use-watch-toggle';
import NewsletterModal from '@/features/marketplace/coming-soon/components/newsletter-modal';
import SubscriptionSuccessModal from '../../../../../components/SubscriptionSuccessModal';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useNewsletterSubscription, NEWSLETTER_SOURCE } from '@/hooks/use-newsletter-subscription';
import SubscriptionButton from '../house-details/components/SubscriptionButton';
import { HeaderVisibilityProvider } from '@/contexts/header-visibility-context';
import { isBrowser } from '@/utils/util_helpers/ssr';

type TranslationsType = {
  [locale: string]: string | null | undefined;
};

interface ComingSoonProps {
  city: string;
  cityId: number;
  name_translations?: TranslationsType;
  about_translations?: TranslationsType;
  pre_launch_background?: string;
  sketch_image?: string;
}

const ComingSoonContent: React.FC<ComingSoonProps> = React.memo((props) => {
  const {
    city,
    cityId,
    name_translations = {},
    about_translations = {},
    pre_launch_background,
    sketch_image,
  } = props;

  // Using placehold.co service for a placeholder image with "City" text
  const placeholderImage = `https://placehold.co/1920x1080/e9ecef/495057?text=${city}`;
  const placeholderSketchImage = 'https://placehold.co/600x400/e9ecef/495057?text=Sketch';

  const { currentLocale } = useLocale();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const { t } = useTranslation();
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const currentUser = useCurrentUser();
  const { subscribe, unsubscribe, isLoading: isSubscribing } = useNewsletterSubscription();

  const cityDetail = props;

  const { data: colocationsData, isLoading } = useColocations({ citySlug: city.toLowerCase() });

  const { handleWatchToggle } = useWatchToggle(city);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      // Show left button when scrolled past the start
      setCanScrollLeft(scrollLeft > 0);
      // Show right button when there's more content to scroll
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      const scrollAmount = direction === 'left' ? -containerWidth : containerWidth;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const targetScroll = currentScroll + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: 'smooth',
      });

      // Force check scroll state after scrolling
      setTimeout(handleScroll, 100);
    }
  };

  // Update initial scroll state when data changes
  React.useEffect(() => {
    if (scrollContainerRef.current && colocationsData?.colocations) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      const contentWidth = colocationsData.colocations.length * 304; // 280px card + 24px gap
      setCanScrollRight(contentWidth > containerWidth);
      setCanScrollLeft(false); // Reset left scroll state when data changes
    }
  }, [colocationsData?.colocations]);

  // Add scroll event listener and check initial state
  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Check initial scroll state
      handleScroll();
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, []);

  const handleSubscribe = async () => {
    if (isSubscribing) return; // a double click would send a second Lead

    if (!currentUser && !isSubscribed) {
      setIsNewsletterModalOpen(true);
      return;
    }

    try {
      if (isSubscribed) {
        const subscribedEmail = isBrowser() ? sessionStorage.getItem('subscribed_email') : null;
        await unsubscribe({
          email: subscribedEmail || currentUser?.email || '',
          cityId: cityId,
        });
        setIsSubscribed(false);

        if (isBrowser()) {
          sessionStorage.removeItem('subscribed_email');
        }
      } else {
        if (!currentUser) return;

        await subscribe({
          email: currentUser.email,
          firstName: currentUser.first_name,
          lastName: currentUser.last_name,
          cityId: cityId,
          language: currentLocale,
          source: NEWSLETTER_SOURCE.COMING_SOON_DIRECT,
        });
        setIsSubscribed(true);
        setIsSuccessModalOpen(true);
      }
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const handleNewsletterSuccess = () => {
    setIsNewsletterModalOpen(false);
    setIsSubscribed(true);
    setIsSuccessModalOpen(true);
  };

  return (
    <>
      <HeaderVisibilityProvider>
        <MarketPlaceHeader />
      </HeaderVisibilityProvider>
      <div className={composeClassName('min-h-screen bg-white', PADDING_TOP_HEADER_HEIGHT)}>
        {/* Hero Section */}
        <div className="relative w-full overflow-hidden bg-gray-100" style={{ height: '520px' }}>
          <img
            src={cityDetail?.pre_launch_background_url || pre_launch_background || placeholderImage}
            alt={city}
            className="h-full w-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = placeholderImage;
            }}
          />
          <div className="absolute inset-0 bg-opacity-40" />
        </div>

        {/* City Info Section */}
        <Box sx={{ mx: 'auto', px: 0, py: 6 }} className="w-[calc(100%-theme(space.7))] sm:w-[80%]">
          <div className="mb-[13rem] flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
            <div className="w-full md:w-[80%]">
              <h1 className="font-sans text-[22px] font-bold text-N900">
                {t('js.components.coming_soon_page.title')}
                <br />
              </h1>
              <h2 style={{ fontSize: '72px' }} className="font-sans font-bold text-N900">
                {cityDetail?.name_translations?.[currentLocale] ||
                  name_translations[currentLocale] ||
                  ''}
              </h2>
              <div
                style={{ fontSize: '18px' }}
                className="font-sans font-medium leading-relaxed text-N900"
                dangerouslySetInnerHTML={{
                  __html:
                    cityDetail?.about_translations?.[currentLocale] ||
                    about_translations[currentLocale] ||
                    '',
                }}
              />
            </div>
            <div className="sm:w-[45%]">
              <div className="relative aspect-square w-full">
                {/* Image positioned underneath */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <img
                    src={cityDetail?.sketch_image_url || sketch_image || placeholderSketchImage}
                    alt="City illustration"
                    width={500}
                    height={500}
                    className="z-10 h-auto max-h-full w-auto max-w-full object-contain"
                  />
                </div>

                {/* Circular background overlay positioned on top with mix-blend-mode */}
                {cityDetail?.sketch_image_url ||
                  (sketch_image && (
                    <div
                      style={{
                        width: '75%',
                        height: '70%',
                        left: '15%',
                        top: '10%',
                      }}
                      className="absolute z-0 rounded-full bg-[#FFFAE9] opacity-70 mix-blend-multiply"
                    ></div>
                  ))}
              </div>
            </div>
          </div>

          {/* Available Houses Section */}
          {colocationsData?.colocations && colocationsData?.colocations?.length > 0 && (
            <>
              <Typography className="mb-4 font-sans text-[22px] font-bold text-N900">
                {t('js.components.coming_soon_page.available_soon_in_city')}
              </Typography>

              <Box sx={{ position: 'relative' }}>
                {canScrollLeft && (
                  <Box
                    onClick={() => scroll('left')}
                    sx={{
                      position: 'absolute',
                      left: -20,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      cursor: 'pointer',
                    }}
                  >
                    <IconSaxIcon name="arrow-left" size={20} strokeColor="#1A1A1A" />
                  </Box>
                )}

                <Box
                  ref={scrollContainerRef}
                  sx={{
                    display: 'flex',
                    gap: 3,
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    pb: 2,
                    minHeight: '400px',
                  }}
                >
                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}
                  {!isLoading &&
                    colocationsData.colocations.map((colocation) => (
                      <Box key={colocation.id} sx={{ minWidth: 280, flexShrink: 0 }}>
                        <HouseCard
                          colocationSlug={colocation.slug}
                          onWatchToggle={handleWatchToggle}
                          isFavorite={colocation.is_watched}
                          image={colocation.image}
                          title={colocation.name}
                          price={colocation.price}
                          availableDate={colocation.available_on}
                          virtualTourUrl={colocation.virtual_tour_url}
                          isComingSoon
                        />
                      </Box>
                    ))}
                </Box>

                {canScrollRight && (
                  <Box
                    onClick={() => scroll('right')}
                    sx={{
                      position: 'absolute',
                      right: -20,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      cursor: 'pointer',
                    }}
                  >
                    <IconSaxIcon name="arrow-right" size={20} strokeColor="#1A1A1A" />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Sticky Subscribe Button */}
        <div
          className="fixed bottom-0 left-0 right-0 z-50 bg-white p-3 shadow-longShadow"
          style={{ position: 'fixed', bottom: 0, width: '100%' }}
        >
          <div className="mx-auto flex items-center justify-between sm:w-[80%]">
            <SubscriptionButton
              onSubscribe={handleSubscribe}
              isSubscribed={isSubscribed}
              currentUser={currentUser}
            />
          </div>
        </div>
      </div>
      <Footer />

      <NewsletterModal
        isOpen={isNewsletterModalOpen}
        onClose={() => setIsNewsletterModalOpen(false)}
        onSuccess={handleNewsletterSuccess}
        cityId={cityId}
        city={city}
        displayPhoneNumber={true}
        phoneRequired={false}
        source={NEWSLETTER_SOURCE.COMING_SOON_MODAL}
      />

      <SubscriptionSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
      />
    </>
  );
});

ComingSoonContent.displayName = 'ComingSoonContent';
ComingSoonContent.propTypes = {
  city: PropTypes.string.isRequired,
  cityId: PropTypes.number.isRequired,
  name_translations: PropTypes.objectOf(PropTypes.string),
  about_translations: PropTypes.objectOf(PropTypes.string),
  pre_launch_background: PropTypes.string,
  sketch_image: PropTypes.string,
};

const ComingSoon: React.FC<ComingSoonProps> = React.memo((props) => {
  return (
    <ReactQueryProvider>
      <ToastProvider />
      <AuthContextProvider>
        <CitiesListContextProvider>
          <ComingSoonContent {...props} />
        </CitiesListContextProvider>
      </AuthContextProvider>
    </ReactQueryProvider>
  );
});

ComingSoon.displayName = 'ComingSoon';
ComingSoon.propTypes = {
  city: PropTypes.string.isRequired,
  cityId: PropTypes.number.isRequired,
  name_translations: PropTypes.objectOf(PropTypes.string),
  about_translations: PropTypes.objectOf(PropTypes.string),
  pre_launch_background: PropTypes.string,
  sketch_image: PropTypes.string,
};

export { ComingSoon };
